import { get, getBlob, patch, post, remove } from "../http-client/client";
import { ApiResponse } from "../http-client/Response";

// ============================================================================
// INTERFACES
// ============================================================================

export interface SourceLocation {
  quote: string;
  startIndex: number;
  endIndex: number;
  location: {
    paragraphIndex?: number;
    sheet?: string;
    row?: number;
    column?: string;
    page?: number;
  };
  confidence?: 'high' | 'medium' | 'low';
}

export interface Rule {
  id: string;
  name: string;
  instructions: string;
  comments?: string;
  enableComments: boolean;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  sourceLocations?: SourceLocation[];
}

export interface Playbook {
  id: string;
  name: string;
  agreementType: string;
  isPrivate: boolean;
  /** True when installed from the curated library (a workspace-approved playbook). */
  isApproved?: boolean;
  createdAt: Date;
  updatedAt: Date;
  userId: string;
  organizationId: string;
  rules: Rule[];
  sourceDocumentS3Key?: string;
  sourceDocumentType?: 'docx' | 'xlsx' | 'pdf' | 'txt';
  sourceDocumentMetadata?: {
    pageCount?: number;
    sheetNames?: string[];
    paragraphCount?: number;
  };
}

export interface SourceDocumentResponse {
  type: string;
  metadata: {
    pageCount?: number;
    sheetNames?: string[];
    paragraphCount?: number;
  };
}

// ============================================================================
// REQUEST DTOS
// ============================================================================

export interface CreateRuleRequest {
  name: string;
  instructions: string;
  comments?: string;
  enableComments?: boolean;
  isActive?: boolean;
}

export interface CreatePlaybookRequest {
  name: string;
  agreementType: string;
  isPrivate?: boolean;
  rules?: CreateRuleRequest[];
}

export interface CreatePlaybookAIRequest {
  userPrompt: string;
  isPrivate?: boolean;
}

export interface UpdateRuleRequest {
  id?: string;
  name?: string;
  instructions?: string;
  comments?: string;
  enableComments?: boolean;
  isActive?: boolean;
}

export interface UpdatePlaybookRequest {
  name?: string;
  agreementType?: string;
  isPrivate?: boolean;
  rules?: UpdateRuleRequest[];
}

// ============================================================================
// API SERVICE FUNCTIONS
// ============================================================================

/**
 * Get all playbooks accessible to the user (private + organization-wide)
 */
export const getPlaybooks = (): Promise<ApiResponse<Playbook[]>> => {
  return get<Playbook[]>("/playbooks", null);
};

/**
 * Get a specific playbook by ID
 */
export const getPlaybook = (playbookId: string): Promise<ApiResponse<Playbook>> => {
  return get<Playbook>(`/playbooks/${playbookId}`, null);
};

/**
 * Create a new playbook manually
 */
export const createPlaybook = (data: CreatePlaybookRequest): Promise<ApiResponse<Playbook>> => {
  return post<Playbook>("/playbooks", data, null);
};

/**
 * Create a new playbook using AI
 */
export const createPlaybookWithAI = async (data: CreatePlaybookAIRequest): Promise<ApiResponse<Playbook>> => {
  const started = await post<PlaybookJob>("/playbook-jobs/ai", data, null);
  return waitForPlaybookJob(started);
};

interface PlaybookJob {
  id: string;
  status: "processing" | "completed" | "failed";
  playbook: Playbook | null;
  error: string | null;
}

const JOB_POLL_MS = 2500;
const JOB_MAX_WAIT_MS = 20 * 60 * 1000;

/**
 * Creating a playbook from a long document takes minutes, longer than one
 * request may stay open, so the server runs it as a job and we poll it.
 */
const waitForPlaybookJob = async (
  started: ApiResponse<PlaybookJob>,
): Promise<ApiResponse<Playbook>> => {
  if (!started.success || !started.data) {
    return { success: false, status: started.status, errors: started.errors };
  }
  const deadline = Date.now() + JOB_MAX_WAIT_MS;
  let job = started.data;
  while (job.status === "processing" && Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, JOB_POLL_MS));
    const res = await get<PlaybookJob>(`/playbook-jobs/${job.id}`, null);
    // A failed poll (e.g. a network blip) is retried until the deadline
    if (res.success && res.data) job = res.data;
  }
  if (job.status === "completed" && job.playbook) {
    return { success: true, status: 200, data: job.playbook };
  }
  return {
    success: false,
    errors: [
      {
        title:
          job.error ||
          (job.status === "processing"
            ? "This is taking longer than expected. Check your playbooks in a few minutes."
            : "Failed to create the playbook"),
      },
    ],
  };
};

/**
 * Update an existing playbook
 */
export const updatePlaybook = (
  playbookId: string,
  data: UpdatePlaybookRequest
): Promise<ApiResponse<Playbook>> => {
  return patch<Playbook>(`/playbooks/${playbookId}`, data, null);
};

/**
 * Delete a playbook
 */
export const deletePlaybook = (playbookId: string): Promise<ApiResponse<void>> => {
  return remove<void>(`/playbooks/${playbookId}`, null);
};

/**
 * Import a playbook from a file
 */
export const importPlaybook = async (
  file: File,
  isPrivate: boolean
): Promise<ApiResponse<Playbook>> => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("isPrivate", String(isPrivate));

  const started = await post<PlaybookJob>("/playbook-jobs/import", formData, null);
  return waitForPlaybookJob(started);
};

/**
 * Get source document metadata for a playbook
 */
export const getPlaybookSourceDocument = (
  playbookId: string
): Promise<ApiResponse<SourceDocumentResponse>> => {
  return get<SourceDocumentResponse>(`/playbooks/${playbookId}/source-document`, null);
};

/**
 * Get source document file as a blob (proxied through backend, no CORS issues)
 */
export const getPlaybookSourceDocumentFile = (
  playbookId: string
): Promise<ApiResponse<Blob>> => {
  return getBlob(`/playbooks/${playbookId}/source-document/file`);
};

// ============================================================================
// HELPER FUNCTIONS FOR RULES MANAGEMENT
// ============================================================================

/**
 * Helper to add a rule to a playbook
 * Fetches the playbook, adds the rule to the rules array, and updates
 */
export const addRuleToPlaybook = async (
  playbookId: string,
  rule: CreateRuleRequest
): Promise<ApiResponse<Playbook>> => {
  // Fetch current playbook
  const playbookResponse = await getPlaybook(playbookId);

  if (!playbookResponse.success || !playbookResponse.data) {
    return playbookResponse as ApiResponse<Playbook>;
  }

  const playbook = playbookResponse.data;

  // Add new rule to existing rules
  const updatedRules: UpdateRuleRequest[] = [
    ...playbook.rules.map((r) => ({
      id: r.id,
      name: r.name,
      instructions: r.instructions,
      comments: r.comments,
      enableComments: r.enableComments,
      isActive: r.isActive,
    })),
    {
      name: rule.name,
      instructions: rule.instructions,
      comments: rule.comments,
      enableComments: rule.enableComments ?? true,
      isActive: rule.isActive ?? true,
    },
  ];

  // Update playbook with new rules array
  return updatePlaybook(playbookId, { rules: updatedRules });
};

/**
 * Helper to update a specific rule in a playbook
 */
export const updateRuleInPlaybook = async (
  playbookId: string,
  ruleId: string,
  updates: Partial<UpdateRuleRequest>
): Promise<ApiResponse<Playbook>> => {
  // Fetch current playbook
  const playbookResponse = await getPlaybook(playbookId);

  if (!playbookResponse.success || !playbookResponse.data) {
    return playbookResponse as ApiResponse<Playbook>;
  }

  const playbook = playbookResponse.data;

  // Update the specific rule
  const updatedRules: UpdateRuleRequest[] = playbook.rules.map((r) =>
    r.id === ruleId
      ? {
          id: r.id,
          name: updates.name ?? r.name,
          instructions: updates.instructions ?? r.instructions,
          comments: updates.comments ?? r.comments,
          enableComments: updates.enableComments ?? r.enableComments,
          isActive: updates.isActive ?? r.isActive,
        }
      : {
          id: r.id,
          name: r.name,
          instructions: r.instructions,
          comments: r.comments,
          enableComments: r.enableComments,
          isActive: r.isActive,
        }
  );

  // Update playbook with modified rules array
  return updatePlaybook(playbookId, { rules: updatedRules });
};

/**
 * Helper to delete a rule from a playbook
 */
export const deleteRuleFromPlaybook = async (
  playbookId: string,
  ruleId: string
): Promise<ApiResponse<Playbook>> => {
  // Fetch current playbook
  const playbookResponse = await getPlaybook(playbookId);

  if (!playbookResponse.success || !playbookResponse.data) {
    return playbookResponse as ApiResponse<Playbook>;
  }

  const playbook = playbookResponse.data;

  // Remove the rule from the array
  const updatedRules: UpdateRuleRequest[] = playbook.rules
    .filter((r) => r.id !== ruleId)
    .map((r) => ({
      id: r.id,
      name: r.name,
      instructions: r.instructions,
      comments: r.comments,
      enableComments: r.enableComments,
      isActive: r.isActive,
    }));

  // Update playbook with filtered rules array
  return updatePlaybook(playbookId, { rules: updatedRules });
};
